import React, { useState, useEffect } from 'react';
import { 
    Box, 
    Button,  
    Dialog, 
    DialogTitle, 
    DialogContent, 
    DialogActions, 
    FormControl, 
    InputLabel, 
    Select, 
    MenuItem,
} from '@mui/material';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns'
import { de, enGB } from 'date-fns/locale';
import FlexBetween from 'components/FlexBetween';


const ProductInventoryFilterDialog = ({ 
    open, 
    onClose, 
    onApply, 
    supplierNameOptions, 
    productNameOptions, 
    currentFilters, 
}) => {
    const locales = { 'en-us': undefined, 'en-gb': enGB, de };
    const [cleared, setCleared] = useState(false);

    useEffect(() => {
        if (cleared) {
            const timeout = setTimeout(() => {
                setCleared(false);
            }, 1500);

            return () => clearTimeout(timeout);
        }
        return () => {};
    }, [cleared]);

    const ITEM_HEIGHT = 60;
    const ITEM_PADDING_TOP = 8;

    const initialFilterValues = {
        date: null,
        supplierName: [],
        productName: [],
    };

    const [filterValues, setFilterValues] = useState(() => {
        if (!currentFilters) return initialFilterValues;
        return {
            ...initialFilterValues,
            ...currentFilters,
            supplierName: currentFilters.supplierName ?? [],
            productName: currentFilters.productName ?? [],
        };
    });

    const handleDateChange = (date) => {
        setFilterValues(prev => ({ 
            ...prev, 
            date: date 
        }));
    };

    const handleFilterChange = (field) => (event) => {
        const value = event.target.value;
        setFilterValues(prev => ({
            ...prev,
            [field]: value, 
        }));
    };     

    const handleCancel = () => {
        setFilterValues(initialFilterValues);  
        onApply(null);
        onClose();  
    };
    
    const handleApply = () => {
        const appliedFilters = {
            ...filterValues,
            supplierName: filterValues.supplierName.length ? filterValues.supplierName : null,
            productName: filterValues.productName.length ? filterValues.productName : null,
        };        
        onApply(appliedFilters);
        onClose();
    };    

    const handleKeyPress = (event) => {
        if (event.key === 'Enter') {
            handleApply();
        }
    };

    return (   
        <LocalizationProvider 
            dateAdapter={AdapterDateFns}
            adapterLocale={locales['en-gb']}
        >     
            <Dialog 
                open={open} 
                maxWidth="sm" 
                fullWidth
                onClose={handleCancel}
            >
                <DialogTitle>
                    Filter
                </DialogTitle>
                <DialogContent onKeyDown={handleKeyPress}>
                    <Box display="flex" flexDirection="column" gap={2}>
                        <FlexBetween
                            gap={2}
                            sx={{ marginTop: '6px' }}
                        >
                            <DatePicker
                                label="Date"
                                value={filterValues.date}
                                onChange={handleDateChange}
                                format="dd/MM/yyyy"
                                sx={{ width: '50%' }}
                                slotProps={{
                                    field: { clearable: true, onClear: () => setCleared(true) },
                                }}                                
                            />

                            <FormControl sx={{ width: '50%' }} >
                                <InputLabel>Supplier Name</InputLabel>
                                <Select
                                    multiple
                                    label="Supplier Name"
                                    value={filterValues.supplierName}
                                    onChange={handleFilterChange('supplierName')}
                                    renderValue={(selected) => selected.join(', ')}
                                    MenuProps={{
                                        PaperProps: {
                                            style: {
                                                maxHeight: ITEM_HEIGHT * 4.5 + ITEM_PADDING_TOP,
                                            },
                                        },
                                    }}
                                >
                                    {supplierNameOptions.map((supplier, index) => (
                                        <MenuItem key={index} value={supplier}>
                                            {supplier}
                                        </MenuItem>
                                    ))}
                                </Select>
                            </FormControl>
                        </FlexBetween>
                         
                        <FormControl fullWidth>
                            <InputLabel>Product Name</InputLabel>
                            <Select
                                multiple
                                label="Product Name"
                                value={filterValues.productName}
                                onChange={handleFilterChange('productName')}
                                renderValue={(selected) => selected.join(', ')}
                                MenuProps={{
                                    PaperProps: {
                                        style: {
                                            maxHeight: ITEM_HEIGHT * 4.5 + ITEM_PADDING_TOP,
                                        },
                                    },
                                }}
                            >
                                {productNameOptions.map((product, index) => (
                                    <MenuItem key={index} value={product}>
                                        {product}
                                    </MenuItem>
                                ))}
                            </Select>
                        </FormControl> 
                    </Box>
                </DialogContent>
                <DialogActions>
                    <Button 
                        variant="outlined" 
                        color="secondary" 
                        onClick={handleCancel} 
                    >
                        Cancel
                    </Button>
                    <Button 
                        variant="outlined"
                        onClick={handleApply} 
                    >
                        Apply
                    </Button>
                </DialogActions>
            </Dialog>
        </LocalizationProvider>
    );
};

export default ProductInventoryFilterDialog;
